import { usePersonInfo } from '@/data/Content/PersonInfo';
import { useUser } from '@/data/User';
import { parseUserPreferredSizes } from 'integration/hooks/usePreferredSizes';
import { useMemo } from 'react';
import type { DppMemberPromotion } from '@/data/dpp/types';

export type PersonalSizes = Partial<
	Record<'tops' | 'shirts' | 'pants' | 'footwear' | 'caps', string>
>;

const SIZE_LABELS = ['FOOTWEAR', 'TOPS', 'SHIRTS', 'PANTS', 'CAPS'];

export const personalSizesFromProfile = (sizes?: string): PersonalSizes | undefined => {
	const chosen = parseUserPreferredSizes(sizes).filter(
		({ label, value }) => SIZE_LABELS.includes(label) && value
	);

	return chosen.length
		? Object.fromEntries(chosen.map(({ label, value }) => [label.toLowerCase(), value]))
		: undefined;
};

const toPersonalSizeValue = (value: string) => {
	const number = Number(value);
	return Number.isFinite(number) ? String(number) : value;
};

export const personalSizesFromDpp = (
	sizes?: DppMemberPromotion['sizes']
): PersonalSizes | undefined => {
	const chosen = Object.entries(sizes ?? {}).flatMap(([label, size]) => {
		const value = size?.value?.trim();
		return SIZE_LABELS.includes(label) && value
			? [[label.toLowerCase(), toPersonalSizeValue(value)]]
			: [];
	});
	return chosen.length ? Object.fromEntries(chosen) : undefined;
};

export const useRecomPersonalSizes = () => {
	const { user } = useUser();
	const isLoggedIn = !!user?.isLoggedIn;
	const { personInfo, isLoading } = usePersonInfo(isLoggedIn);
	const sizes = useMemo(
		() => personalSizesFromProfile(personInfo?.x_userProfileField1),
		[personInfo?.x_userProfileField1]
	);

	return { sizes, loading: isLoggedIn && isLoading };
};
